import React, { useCallback, useMemo } from 'react'
import { Table, Select, Typography } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import {
  PAYMENT_ASSIGNMENT_STATUS_LABELS,
  PaymentAssignmentStatusId,
} from '../Utils/types'
import { DEFAULT_STATUSES } from './payment-assignment-metrics.config'

const { Text } = Typography

const fmt = (n: number) => n.toLocaleString()

interface CarrierPerspectiveMetricsProps {
  data: Record<string, Record<string, number>> | undefined
  loading?: boolean
  carrierNames: string[]
  carrierNamesLoading?: boolean
  selectedCarriers: string[]
  onSelectedCarriersChange: (carriers: string[]) => void
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

interface CarrierRow {
  key: string
  label: string
  isGrandTotal?: boolean
  [status: string]: string | number | boolean | undefined
}

const SectionCard = styled.div`
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #fafafa;
  }
`

const ExpandLink = styled.span`
  color: #1890ff;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const FilterRow = styled.div`
  padding: 8px 24px 16px;
`

function collectStatuses(
  data: Record<string, Record<string, number>>
): string[] {
  const seen = new Set<string>(DEFAULT_STATUSES)
  const extra: string[] = []
  for (const counts of Object.values(data)) {
    for (const s of Object.keys(counts)) {
      if (!seen.has(s)) {
        seen.add(s)
        extra.push(s)
      }
    }
  }
  return [...DEFAULT_STATUSES, ...extra]
}

function buildColumns(statuses: string[]): ColumnsType<CarrierRow> {
  const cols: ColumnsType<CarrierRow> = [
    {
      title: 'Carrier',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left' as const,
      width: 200,
      render: (text: string, record: CarrierRow) =>
        record.isGrandTotal ? <Text strong>{text}</Text> : text,
    },
  ]

  for (const status of statuses) {
    const label =
      PAYMENT_ASSIGNMENT_STATUS_LABELS[status as PaymentAssignmentStatusId] ??
      status.replace(/_/g, ' ')
    cols.push({
      title: label.toUpperCase(),
      dataIndex: status,
      key: status,
      align: 'center' as const,
      width: 170,
      render: (value: number | undefined, record: CarrierRow) => {
        const num = value ?? 0
        return record.isGrandTotal ? <Text strong>{fmt(num)}</Text> : fmt(num)
      },
    })
  }

  cols.push({
    title: 'TOTAL',
    key: '__total__',
    fixed: 'right' as const,
    align: 'center' as const,
    width: 120,
    render: (_: unknown, record: CarrierRow) => {
      const total = statuses.reduce(
        (sum, s) => sum + (Number(record[s]) || 0),
        0
      )
      return record.isGrandTotal ? <Text strong>{fmt(total)}</Text> : fmt(total)
    },
  })

  return cols
}

function buildCarrierRows(
  data: Record<string, Record<string, number>>,
  selectedCarriers: string[],
  statuses: string[]
): CarrierRow[] {
  const allEntries = Object.entries(data)

  const grandTotals: Record<string, number> = {}
  for (const [, counts] of allEntries) {
    for (const s of statuses) {
      grandTotals[s] = (grandTotals[s] ?? 0) + (counts[s] ?? 0)
    }
  }

  const grandTotal: CarrierRow = {
    key: 'grand-total',
    label: 'Grand Total',
    isGrandTotal: true,
  }
  for (const s of statuses) {
    grandTotal[s] = grandTotals[s] ?? 0
  }

  if (selectedCarriers.length === 0) {
    return [grandTotal]
  }

  const rows: CarrierRow[] = allEntries
    .filter(([carrier]) => selectedCarriers.includes(carrier))
    .map(([carrier, counts]) => {
      const row: CarrierRow = {
        key: carrier,
        label: carrier,
      }
      for (const s of statuses) {
        row[s] = counts[s] ?? 0
      }
      return row
    })

  rows.push(grandTotal)
  return rows
}

export default function CarrierPerspectiveMetrics({
  data,
  loading,
  carrierNames,
  carrierNamesLoading,
  selectedCarriers,
  onSelectedCarriersChange,
  expanded,
  onExpandedChange,
}: CarrierPerspectiveMetricsProps) {
  const carrierOptions = useMemo(
    () =>
      [...carrierNames]
        .map((name) => ({ label: name, value: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [carrierNames]
  )

  const statuses = useMemo(
    () => (data ? collectStatuses(data) : [...DEFAULT_STATUSES]),
    [data]
  )

  const columns = useMemo(() => buildColumns(statuses), [statuses])

  const rows = useMemo(
    () => (data ? buildCarrierRows(data, selectedCarriers, statuses) : []),
    [data, selectedCarriers, statuses]
  )

  const handleCarrierChange = useCallback(
    (values: string[]) => {
      onSelectedCarriersChange(values)
    },
    [onSelectedCarriersChange]
  )

  const selectedCount = selectedCarriers.length

  return (
    <SectionCard>
      <SectionHeader onClick={() => onExpandedChange(!expanded)}>
        <Text strong style={{ fontSize: 15 }}>
          Carrier Perspective
        </Text>
        <ExpandLink>
          {expanded ? (
            <>
              <DownOutlined /> Collapse
            </>
          ) : (
            <>
              <RightOutlined /> Expand
            </>
          )}
        </ExpandLink>
      </SectionHeader>

      {expanded && (
        <>
          <FilterRow>
            <Text style={{ display: 'block', marginBottom: 4 }}>
              Select Carriers to Display:
            </Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select carriers..."
              value={selectedCarriers}
              onChange={handleCarrierChange}
              options={carrierOptions}
              loading={carrierNamesLoading}
              maxTagCount="responsive"
              maxTagPlaceholder={() =>
                `${selectedCount} carrier${selectedCount === 1 ? '' : 's'} selected`
              }
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </FilterRow>

          <Table<CarrierRow>
            columns={columns}
            dataSource={rows}
            pagination={false}
            loading={loading}
            scroll={{ x: 'max-content' }}
            size="small"
            bordered
            rowClassName={(record) =>
              record.isGrandTotal ? 'grand-total-row' : ''
            }
          />
        </>
      )}
    </SectionCard>
  )
}
